import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { IconPlus, IconSearch, IconEdit, IconTrash } from './Icons';
import ExcelJS from 'exceljs';
import { sendDepartmentManagerAssignedEmail, getPortalUrlForEmails } from '../services/emailService';
import './Departments.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const departmentsSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const COLUMNS = [
  { key: 'code', label: 'Code' },
  { key: 'name', label: 'Name' },
  { key: 'manager', label: 'Manager' },
  { key: 'location', label: 'Location' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'status', label: 'Status' },
];

const emptyForm = () => ({
  code: '',
  name: '',
  manager_profile_id: '',
  location: '',
  costCenter: '',
  email: '',
  phone: '',
  parent: '',
  status: 'Active',
  description: '',
});

export default function Departments() {
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null); // error shown inside Add/Edit modal
  const [exporting, setExporting] = useState(false);

  const managersById = useMemo(
    () =>
      managers.reduce((acc, m) => {
        acc[m.id] = m;
        return acc;
      }, {}),
    [managers],
  );

  const mapRow = (row) => ({
    id: row.id,
    code: row.code || '',
    name: row.name || '',
    manager: row.manager || '',
    manager_profile_id: row.manager_profile_id || null,
    location: row.location || '',
    email: row.email || '',
    phone: row.phone || '',
    status: row.status || 'Active',
    description: row.description || '',
  });

  const FETCH_TIMEOUT_MS = 12000;

  const runWakeUp = useCallback(() => {
    return Promise.race([
      departmentsSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchDepartments = useCallback(async (mountedRef) => {
    const isMounted = () => !mountedRef || mountedRef.current;
    setLoading(true);
    setError(null);
    try {
      const fetchPromise = departmentsSupabase.from('departments').select('*').order('name');
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const result = await Promise.race([fetchPromise, timeoutPromise]);
      if (!isMounted()) return;
      const { data, error } = result;
      if (error) {
        setError(error.message);
        setDepartments([]);
      } else {
        setDepartments((data || []).map(mapRow));
      }
    } catch (e) {
      if (isMounted()) {
        setError(e?.message || 'Failed to load departments.');
        setDepartments([]);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, []);

  const fetchManagers = useCallback(async (mountedRef) => {
    const isMounted = () => !mountedRef || mountedRef.current;
    const { data, error } = await departmentsSupabase
      .from('profiles')
      .select('id, full_name, email')
      .order('full_name', { ascending: true, nullsFirst: false });
    if (!isMounted()) return;
    if (error) {
      console.error('[Departments] fetchManagers error', error);
      setManagers([]);
    } else {
      setManagers(data || []);
    }
  }, []);

  const location = useLocation();
  const mountedRef = useRef(true);

  const refreshSessionAndFetch = useCallback(async () => {
    mountedRef.current = true;
    setError(null);
    try {
      await departmentsSupabase.auth.getSession();
      await runWakeUp();
      await fetchDepartments(mountedRef);
      if (!mountedRef.current) return;
      await fetchManagers(mountedRef);
    } catch (e) {
      setError(e?.message || 'Failed to load');
    }
  }, [runWakeUp, fetchDepartments, fetchManagers]);

  useEffect(() => {
    if (location.pathname !== '/departments') return;
    let cancelled = false;
    mountedRef.current = true;
    (async () => {
      const { error: sessionErr } = await departmentsSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setError(sessionErr.message);
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await fetchDepartments(mountedRef);
      if (cancelled) return;
      await fetchManagers(mountedRef);
    })();
    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [location.pathname, runWakeUp, fetchDepartments, fetchManagers]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const getManagerLabel = (dept) => {
    if (dept.manager_profile_id && managersById[dept.manager_profile_id]) {
      const m = managersById[dept.manager_profile_id];
      return m.full_name || dept.manager || '—';
    }
    return dept.manager || '—';
  };

  const filtered = departments.filter((d) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      d.code.toLowerCase().includes(q) ||
      d.name.toLowerCase().includes(q) ||
      getManagerLabel(d).toLowerCase().includes(q) ||
      d.location.toLowerCase().includes(q)
    );
  });

  const handleExportExcel = async () => {
    if (!filtered.length || exporting) return;
    setExporting(true);
    setError(null);

    const brandHeader = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB45309' } };
    const brandHeaderFont = { color: { argb: 'FFFFFFFF' }, bold: true, name: 'Calibri', size: 11 };
    const brandTitleFont = { bold: true, size: 16, color: { argb: 'FFB45309' }, name: 'Calibri' };
    const brandSubtitleFont = { size: 10, color: { argb: 'FF6B7280' }, name: 'Calibri' };
    const borderThin = {
      top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
    };
    const zebraFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7ED' } };

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'ECDT Portal';
      workbook.created = new Date();
      const sheet = workbook.addWorksheet('Departments', {
        properties: { tabColor: { argb: 'FFB45309' } },
      });

      try {
        const logoUrl = `${process.env.PUBLIC_URL || ''}/logo.png`;
        const res = await fetch(logoUrl);
        if (res.ok) {
          const blob = await res.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const imageId = workbook.addImage({ buffer: arrayBuffer, extension: 'png' });
          sheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 180, height: 48 } });
        }
      } catch (e) {
        console.warn('[Departments] Excel logo fetch failed', e);
      }

      const titleRowIndex = 4;
      const titleRow = sheet.getRow(titleRowIndex);
      titleRow.getCell(1).value = 'Departments';
      titleRow.height = 22;
      titleRow.font = brandTitleFont;
      sheet.mergeCells(titleRowIndex, 1, titleRowIndex, COLUMNS.length);

      const subRowIndex = titleRowIndex + 1;
      const subRow = sheet.getRow(subRowIndex);
      subRow.getCell(1).value = `ECDT Portal · Exported ${new Date().toLocaleString()}`;
      subRow.font = brandSubtitleFont;
      sheet.mergeCells(subRowIndex, 1, subRowIndex, COLUMNS.length);

      const headerRowIndex = subRowIndex + 2;
      const headerRow = sheet.getRow(headerRowIndex);
      COLUMNS.forEach((col, idx) => {
        headerRow.getCell(idx + 1).value = col.label;
      });
      headerRow.height = 20;
      headerRow.eachCell({ includeEmpty: false }, (cell) => {
        cell.fill = brandHeader;
        cell.font = brandHeaderFont;
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
        cell.border = borderThin;
      });

      let rowIndex = headerRowIndex + 1;
      filtered.forEach((d, i) => {
        const row = sheet.getRow(rowIndex);
        COLUMNS.forEach((col, idx) => {
          const cell = row.getCell(idx + 1);
          let val = '';
          switch (col.key) {
            case 'code':
              val = d.code || '';
              break;
            case 'name':
              val = d.name || '';
              break;
            case 'manager':
              val = getManagerLabel(d) || '';
              break;
            case 'location':
              val = d.location || '';
              break;
            case 'email':
              val = d.email || '';
              break;
            case 'phone':
              val = d.phone || '';
              break;
            case 'status':
              val = d.status || '';
              break;
            default:
              break;
          }
          cell.value = val;
          cell.font = { name: 'Calibri', size: 11, color: { argb: 'FF1F2937' } };
          cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
          cell.border = borderThin;
          if (i % 2 === 1) {
            cell.fill = zebraFill;
          }
        });
        row.height = 18;
        rowIndex += 1;
      });

      const colWidths = [14, 28, 24, 22, 30, 16, 12];
      sheet.columns = colWidths.slice(0, COLUMNS.length).map((w) => ({ width: w }));

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `departments-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[Departments] export failed', e);
      setError(e?.message || 'Export failed. Try again.');
    } finally {
      setExporting(false);
    }
  };

  const openAdd = () => {
    setSelected(null);
    setForm(emptyForm());
    setSaveError(null);
    setShowAddModal(true);
    runWakeUp();
  };

  const openEdit = (dept) => {
    setSelected(dept);
    setForm({
      code: dept.code,
      name: dept.name,
      manager_profile_id: dept.manager_profile_id || '',
      location: dept.location,
      email: dept.email,
      phone: dept.phone,
      status: dept.status,
      description: dept.description,
    });
    setSaveError(null);
    setShowEditModal(true);
  };

  const INSERT_TIMEOUT_MS = 20000;

  const handleSaveNew = async () => {
    const trimmedName = form.name.trim();
    if (!trimmedName) return;
    const managerProfile = form.manager_profile_id
      ? managersById[form.manager_profile_id] || null
      : null;
    const managerName = managerProfile ? managerProfile.full_name : '';

    const payload = {
      code: form.code.trim() || trimmedName.slice(0, 4).toUpperCase(),
      name: trimmedName,
      manager: managerName || null,
      manager_profile_id: form.manager_profile_id || null,
      location: form.location.trim() || null,
      cost_center: form.costCenter.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      parent_department: form.parent.trim() || null,
      status: form.status || 'Active',
      description: form.description.trim() || null,
    };

    setSaving(true);
    setError(null);
    setSaveError(null);

    const runOneInsert = () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), INSERT_TIMEOUT_MS);
      return departmentsSupabase
        .from('departments')
        .insert(payload)
        .select()
        .single()
        .abortSignal(controller.signal)
        .then((result) => {
          clearTimeout(timeoutId);
          return result;
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          if (err?.name === 'AbortError' || err?.message?.toLowerCase().includes('abort')) {
            throw new Error('Request timed out. Try refreshing the page.');
          }
          throw err;
        });
    };

    try {
      await runWakeUp();
      const { data, error } = await runOneInsert();
      if (error) {
        console.error('[Departments] insert error', error);
        setSaveError(error.message);
        setError(error.message);
        return;
      }
      setDepartments((prev) => [...prev, mapRow(data)]);
      setShowAddModal(false);
      {
        const mgrId = form.manager_profile_id;
        if (mgrId) {
          const mp = managersById[mgrId];
          const mgrEmail = (mp?.email || '').trim();
          if (mgrEmail) {
            void sendDepartmentManagerAssignedEmail({
              to: mgrEmail,
              managerName: mp?.full_name,
              departmentName: trimmedName,
              departmentCode: data?.code || payload.code,
              portalUrl: getPortalUrlForEmails(),
            }).then((r) => {
              if (!r.success) console.warn('[Departments] Manager assignment email:', r.error);
            });
          } else {
            console.info('[Departments] No manager email on profile; skipped assignment notification');
          }
        }
      }
    } catch (e) {
      const isTimeout = e?.message?.includes('timed out');
      if (isTimeout) {
        await new Promise((r) => setTimeout(r, 400));
        await runWakeUp();
        try {
          const { data, error } = await runOneInsert();
          if (error) {
            setSaveError(error.message);
            setError(error.message);
            return;
          }
          setDepartments((prev) => [...prev, mapRow(data)]);
          setShowAddModal(false);
          {
            const mgrId = form.manager_profile_id;
            if (mgrId) {
              const mp = managersById[mgrId];
              const mgrEmail = (mp?.email || '').trim();
              if (mgrEmail) {
                void sendDepartmentManagerAssignedEmail({
                  to: mgrEmail,
                  managerName: mp?.full_name,
                  departmentName: trimmedName,
                  departmentCode: data?.code || payload.code,
                  portalUrl: getPortalUrlForEmails(),
                }).then((r) => {
                  if (!r.success) console.warn('[Departments] Manager assignment email:', r.error);
                });
              }
            }
          }
          return;
        } catch (retryErr) {
          console.error('[Departments] insert retry failed', retryErr);
          setSaveError(retryErr?.message || 'Save failed. Try refreshing the page.');
          setError(retryErr?.message || 'Save failed. Try refreshing the page.');
        }
      } else {
        console.error('[Departments] insert failed', e);
        setSaveError(e?.message || 'Save failed. Try refreshing the page.');
        setError(e?.message || 'Save failed. Try refreshing the page.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selected) return;
    const trimmedName = form.name.trim();
    if (!trimmedName) return;
    const managerProfile = form.manager_profile_id
      ? managersById[form.manager_profile_id] || null
      : null;
    const managerName = managerProfile ? managerProfile.full_name : '';

    const payload = {
      code: form.code.trim() || selected.code,
      name: trimmedName,
      manager: managerName || null,
      manager_profile_id: form.manager_profile_id || null,
      location: form.location.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      status: form.status || 'Active',
      description: form.description.trim() || null,
    };

    setSaving(true);
    setError(null);
    setSaveError(null);
    const UPDATE_TIMEOUT_MS = 20000;
    const runOneUpdate = () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), UPDATE_TIMEOUT_MS);
      return departmentsSupabase
        .from('departments')
        .update(payload)
        .eq('id', selected.id)
        .select()
        .single()
        .abortSignal(controller.signal)
        .then((result) => {
          clearTimeout(timeoutId);
          return result;
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          if (err?.name === 'AbortError' || err?.message?.toLowerCase().includes('abort')) {
            throw new Error('Request timed out. Try refreshing the page.');
          }
          throw err;
        });
    };

    try {
      await runWakeUp();
      const { data, error } = await runOneUpdate();
      if (error) {
        console.error('[Departments] update error', error);
        setSaveError(error.message);
        setError(error.message);
        return;
      }
      setDepartments((prev) =>
        prev.map((d) => (d.id === selected.id ? mapRow(data) : d)),
      );
      setShowEditModal(false);
      setSelected(null);
    } catch (e) {
      const isTimeout = e?.message?.includes('timed out');
      if (isTimeout) {
        await new Promise((r) => setTimeout(r, 400));
        await runWakeUp();
        try {
          const { data, error } = await runOneUpdate();
          if (error) {
            setSaveError(error.message);
            setError(error.message);
            return;
          }
          setDepartments((prev) =>
            prev.map((d) => (d.id === selected.id ? mapRow(data) : d)),
          );
          setShowEditModal(false);
          setSelected(null);
          return;
        } catch (retryErr) {
          console.error('[Departments] update retry failed', retryErr);
          setSaveError(retryErr?.message || 'Save failed. Try refreshing the page.');
          setError(retryErr?.message || 'Save failed. Try refreshing the page.');
        }
      } else {
        console.error('[Departments] update failed', e);
        setSaveError(e?.message || 'Save failed. Try refreshing the page.');
        setError(e?.message || 'Save failed. Try refreshing the page.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dept) => {
    if (!window.confirm(`Delete department "${dept.name}"?`)) return;
    setSaving(true);
    const { error } = await departmentsSupabase.from('departments').delete().eq('id', dept.id);
    setSaving(false);
    if (error) {
      setError(error.message);
      return;
    }
    setError(null);
    setDepartments((prev) => prev.filter((d) => d.id !== dept.id));
  };

  return (
    <div className="departments-page">
      <div className="departments-card">
        <div className="departments-header-row">
          <h1 className="departments-title">Departments</h1>
          <div className="departments-header-actions">
            <button
              type="button"
              className="departments-header-btn departments-header-btn-primary"
              onClick={openAdd}
            >
              <IconPlus />
              Add department
            </button>
          </div>
        </div>

        <div className="departments-toolbar">
          <button
            type="button"
            className="departments-toolbar-btn"
            onClick={handleExportExcel}
            disabled={loading || filtered.length === 0 || exporting}
          >
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
          <div className="departments-search-wrap">
            <IconSearch />
            <input
              type="search"
              className="departments-search"
              placeholder="Search departments"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="projects-error" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="projects-loading">Loading departments…</div>
        ) : (
          <div className="departments-table-wrapper">
            <table className="departments-table">
              <thead>
                <tr>
                  <th />
                  {COLUMNS.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} className="departments-empty">
                      No departments found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((dept) => (
                    <tr key={dept.id}>
                      <td>
                        <div className="departments-row-actions">
                          <button
                            type="button"
                            className="departments-row-icon-btn"
                            onClick={() => openEdit(dept)}
                            aria-label="Edit department"
                          >
                            <IconEdit />
                          </button>
                          <button
                            type="button"
                            className="departments-row-icon-btn"
                            onClick={() => handleDelete(dept)}
                            aria-label="Delete department"
                          >
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                      <td>{dept.code}</td>
                      <td>{dept.name}</td>
                      <td>{getManagerLabel(dept)}</td>
                      <td>{dept.location}</td>
                      <td>{dept.email}</td>
                      <td>{dept.phone}</td>
                      <td>
                        <span
                          className={`departments-status-pill ${
                            dept.status === 'Active' ? 'is-active' : 'is-inactive'
                          }`}
                        >
                          {dept.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add department modal */}
      {showAddModal && (
        <div
          className="departments-modal-backdrop"
          onClick={() => {
            setShowAddModal(false);
            setSaveError(null);
          }}
        >
          <div
            className="departments-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="departments-modal-header">
              <h2 className="departments-modal-title">Add department</h2>
              <button
                type="button"
                className="departments-modal-close"
                onClick={() => setShowAddModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="departments-modal-body">
              {saveError && (
                <div className="projects-error" role="alert" style={{ marginBottom: '1rem' }}>
                  {saveError}
                </div>
              )}
              <div className="departments-form-grid">
                <label className="departments-form-label">
                  Code
                  <input
                    className="departments-form-input"
                    placeholder="DEPT code"
                    value={form.code}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  />
                </label>
                <label className="departments-form-label">
                  Name
                  <input
                    className="departments-form-input"
                    placeholder="Department name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </label>
                <label className="departments-form-label">
                  Manager
                  <select
                    className="departments-form-select"
                    value={form.manager_profile_id}
                    onChange={(e) => setForm((f) => ({ ...f, manager_profile_id: e.target.value }))}
                  >
                    <option value="">- Select manager -</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.full_name || m.id}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="departments-form-label">
                  Location
                  <input
                    className="departments-form-input"
                    placeholder="Office / branch"
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  />
                </label>
                <label className="departments-form-label">
                  Email
                  <input
                    className="departments-form-input"
                    type="email"
                    placeholder="Department email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </label>
                <label className="departments-form-label">
                  Phone
                  <input
                    className="departments-form-input"
                    placeholder="Phone"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </label>
                <label className="departments-form-label">
                  Status
                  <select
                    className="departments-form-select"
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </label>
              </div>
              <label className="departments-form-label departments-form-label-wide">
                Description
                <textarea
                  className="departments-form-textarea"
                  placeholder="Short description, responsibilities, notes…"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </label>
            </div>
            <div className="departments-modal-footer">
              <button
                type="button"
                className="departments-footer-btn"
                onClick={() => {
                  setShowAddModal(false);
                  setSaveError(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="departments-footer-btn departments-footer-btn-primary"
                onClick={handleSaveNew}
                disabled={saving || !form.name.trim()}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit department modal */}
      {showEditModal && selected && (
        <div
          className="departments-modal-backdrop"
          onClick={() => {
            setShowEditModal(false);
            setSaveError(null);
          }}
        >
          <div
            className="departments-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="departments-modal-header">
              <h2 className="departments-modal-title">Edit department</h2>
              <button
                type="button"
                className="departments-modal-close"
                onClick={() => {
                  setShowEditModal(false);
                  setSaveError(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="departments-modal-body">
              {saveError && (
                <div className="projects-error" role="alert" style={{ marginBottom: '1rem' }}>
                  {saveError}
                </div>
              )}
              <div className="departments-form-grid">
                <label className="departments-form-label">
                  Code
                  <input
                    className="departments-form-input"
                    value={form.code}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  />
                </label>
                <label className="departments-form-label">
                  Name
                  <input
                    className="departments-form-input"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </label>
                <label className="departments-form-label">
                  Manager
                  <select
                    className="departments-form-select"
                    value={form.manager_profile_id}
                    onChange={(e) => setForm((f) => ({ ...f, manager_profile_id: e.target.value }))}
                  >
                    <option value="">- Select manager -</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.full_name || m.id}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="departments-form-label">
                  Location
                  <input
                    className="departments-form-input"
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  />
                </label>
                <label className="departments-form-label">
                  Email
                  <input
                    className="departments-form-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </label>
                <label className="departments-form-label">
                  Phone
                  <input
                    className="departments-form-input"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </label>
                <label className="departments-form-label">
                  Status
                  <select
                    className="departments-form-select"
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </label>
              </div>
              <label className="departments-form-label departments-form-label-wide">
                Description
                <textarea
                  className="departments-form-textarea"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </label>
            </div>
            <div className="departments-modal-footer">
              <button
                type="button"
                className="departments-footer-btn"
                onClick={() => {
                  setShowEditModal(false);
                  setSaveError(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="departments-footer-btn departments-footer-btn-primary"
                onClick={handleSaveEdit}
                disabled={saving || !form.name.trim()}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

